import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import type { U3129FindProjectSortField, U3129FindProjectSortOrder } from '../../tools/u3129BuildFindProjectSearchCriterias';
import { U3129_FIND_PROJECT_SORT_FIELDS } from '../../tools/u3129BuildFindProjectSearchCriterias';

const COLUMN_LABEL_KEYS: Record<U3129FindProjectSortField, string> = {
  projetNumber: 'lblProject',
  projetName: 'lblProjetName',
  customerCode: 'lblCustomerCode',
  customerName: 'lblCustomerName',
};

export interface U3129ProjectSearchSortableHeaderProps {
  ns: string;
  activeField: U3129FindProjectSortField;
  activeOrder: U3129FindProjectSortOrder;
  onSort: (field: U3129FindProjectSortField) => void;
}

export const U3129ProjectSearchSortableHeader: React.FC<U3129ProjectSearchSortableHeaderProps> = ({
  ns,
  activeField,
  activeOrder,
  onSort,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, field: U3129FindProjectSortField) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSort(field);
      }
    },
    [onSort],
  );

  return (
    <thead className="sticky top-0 z-10 bg-muted">
      <tr>
        {U3129_FIND_PROJECT_SORT_FIELDS.map((field) => {
          const label = t(COLUMN_LABEL_KEYS[field], { ns, defaultValue: field });
          const isActive = activeField === field;
          const ariaSort = isActive ? (activeOrder === 'ASC' ? 'ascending' : 'descending') : 'none';
          const sortHint = isActive
            ? activeOrder === 'ASC'
              ? t('ariaSortColumnDescending', { ns, column: label, defaultValue: `Trier par ${label}, ordre décroissant` })
              : t('ariaSortColumnAscending', { ns, column: label, defaultValue: `Trier par ${label}, ordre croissant` })
            : t('ariaSortColumnActivate', { ns, column: label, defaultValue: `Trier par ${label}` });

          return (
            <th
              key={field}
              scope="col"
              aria-sort={ariaSort}
              title={sortHint}
              tabIndex={0}
              onMouseDown={(e) => {
                // Ne pas laisser le preventDefault du conteneur résultats annuler le click (relance findProject).
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSort(field);
              }}
              onKeyDown={(e) => handleKeyDown(e, field)}
              className="cursor-pointer select-none border-b surface-border px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-200/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
            >
              <span className="inline-flex max-w-full items-center gap-1">
                <span>{label}</span>
                {isActive && activeOrder === 'ASC' && (
                  <span className="inline-block shrink-0 text-xs text-black" aria-hidden>
                    <FaSortAlphaDown size={16} />
                  </span>
                )}
                {isActive && activeOrder === 'DESC' && (
                  <span className="inline-block shrink-0 text-xs text-black" aria-hidden>
                    <FaSortAlphaUp size={16} />
                  </span>
                )}
              </span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};
